import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@material-ui/core';
import { Add, Build, DeleteForever, Lock, LockOpen } from '@material-ui/icons';
import ActionButton from '../../../components/ActionButton';
import CustomTable from '../components/CustomTable';
import EditableCell from '../components/EditableCell';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4, 8),
    boxSizing: 'border-box',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  title: {
    fontSize: 30,
  },
  actionWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  addButton: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  centeredCellContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
}));

const DescrizioneRepartiTable = ({
  addNewRow,
  descrizioneReparti,
  elencoReparti,
  handleCambioRepartoInLocale,
  handleCellChange,
  handleSave,
  loading,
  onClickAttrezzature,
  onRemoveRowDescrizioneReparti,
  isEditingDisabledForConsulente,
  onToggleEditingForConsulente,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const isConsulente =
    localStorage.getItem('superconsulente') === 'false' &&
    localStorage.getItem('consulenteId') !== 'null';

  const isEditingBlocked =
    isConsulente && isEditingDisabledForConsulente.descrizioneReparti;

  const columns = [
    {
      Header: 'Reparti',
      id: 'repartiDescrizione',
      minWidth: 240,
      // sortable: false,
      accessor: (d) => {
        const rep = Array.isArray(elencoReparti)
          ? elencoReparti.find((r) => r.id === d.repartoId)
          : null;
        return rep ? rep.nome : '';
      },
      Cell: (props) => {
        const repartoId = props.original.repartoId;
        const elencoDisponibile =
          Array.isArray(elencoReparti) && elencoReparti.length > 0;
        const isValidReparto =
          elencoDisponibile &&
          elencoReparti.some((item) => item.id === repartoId);

        return (
          <div className={classes.centeredCellContent}>
            {elencoDisponibile ? (
              <Select
                disabled={isEditingBlocked}
                value={isValidReparto ? repartoId : ''}
                variant="outlined"
                onChange={(event) =>
                  handleCambioRepartoInLocale(event.target.value, props.index)
                }
                style={{ paddingTop: 0, width: '100%' }}
              >
                {elencoReparti.map((reparto) => (
                  <MenuItem key={reparto.id} value={reparto.id}>
                    {reparto.nome}
                  </MenuItem>
                ))}
              </Select>
            ) : (
              <Select value="" disabled>
                <MenuItem value="" disabled>
                  Caricamento...
                </MenuItem>
              </Select>
            )}
          </div>
        );
      },
    },
    {
      Header: 'Locale',
      id: 'locale',
      minWidth: 240,
      accessor: (d) => d.nome,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <EditableCell
            value={props.value}
            disabled={isEditingBlocked}
            onChange={(newValue) =>
              handleCellChange(props.index, 'nome', newValue)
            }
          />
        </div>
      ),
    },
    {
      Header: 'Dimensioni',
      id: 'dimensioni',
      accessor: (d) => d.dimensione,
      Cell: (props) => {
        return (
          <div className={classes.centeredCellContent}>
            <EditableCell
              value={props.value}
              disabled={isEditingBlocked}
              onChange={(newValue) => {
                if (/^\d*$/.test(String(newValue))) {
                  handleCellChange(props.index, 'dimensione', newValue);
                }
              }}
            />
          </div>
        );
      },
    },
    {
      Header: 'Attrezzature',
      id: 'attrezzature',
      minWidth: 180,
      sortable: false,
      accessor: (d) => d,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <IconButton
            onClick={() => onClickAttrezzature(props.value, props.index)}
            disabled={isEditingBlocked}
            title="Gestisci attrezzature"
            style={{
              color: isEditingBlocked
                ? theme.palette.disabled.main
                : theme.palette.primary.main,
            }}
          >
            <Build />
          </IconButton>
        </div>
      ),
    },
    {
      Header: 'Elimina',
      id: 'EliminaDescrizioneReparto',
      width: 120,
      sortable: false,
      accessor: (d) => d,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <IconButton
            onClick={() => onRemoveRowDescrizioneReparti(props)}
            disabled={isEditingBlocked}
            style={{
              color: isEditingBlocked
                ? theme.palette.disabled.main
                : theme.palette.primary.main,
            }}
          >
            <DeleteForever />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Paper className={classes.paper}>
      <div className={classes.titleWrapper}>
        <Typography variant="h5" className={classes.title}>
          Descrizione reparti
        </Typography>
        <div className={classes.actionWrapper}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => addNewRow('descrizioneReparti')}
            title="Aggiungi locale"
            disabled={isEditingBlocked}
            className={classes.addButton}
          >
            Locale
          </Button>
          <IconButton
            disabled={isConsulente}
            onClick={() => onToggleEditingForConsulente('descrizioneReparti')}
          >
            {isEditingDisabledForConsulente.descrizioneReparti ? (
              <Lock titleAccess="Modifica bloccata" />
            ) : (
              <LockOpen titleAccess="Modifica consentita" />
            )}
          </IconButton>
        </div>
      </div>

      <CustomTable
        records={descrizioneReparti}
        columns={columns}
        defaultPageSize={5}
        loading={loading}
        filterable={false}
        resizable={true}
        sortable={true}
      />
      <Box mt={3} display="flex" justifyContent="flex-end">
        <ActionButton
          label="Salva modifiche"
          onClick={handleSave}
          disabled={isEditingBlocked}
        />
      </Box>
    </Paper>
  );
};

DescrizioneRepartiTable.propTypes = {
  addNewRow: PropTypes.func.isRequired,
  descrizioneReparti: PropTypes.array.isRequired,
  elencoReparti: PropTypes.array.isRequired,
  handleCambioRepartoInLocale: PropTypes.func.isRequired,
  handleCellChange: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isEditingDisabledForConsulente: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  onClickAttrezzature: PropTypes.func.isRequired,
  onRemoveRowDescrizioneReparti: PropTypes.func.isRequired,
  onToggleEditingForConsulente: PropTypes.func.isRequired,
};

export default DescrizioneRepartiTable;
